/**
 * 전체 공지사항 페이지 — 모든 현장의 공지사항을 한 화면에서 본다.
 * (DB 의 event_type 은 'memo' 그대로다 — 표시 이름만 바꿨다.)
 *
 * 왜 별도 화면인가: 메모는 여러 자리에 조금씩 흩어져 있었다. 현장 목록의 캘린더
 * 아래 보드에는 **날짜 없는** 메모만, 현장 상세의 메모 탭에는 **그 현장** 메모만,
 * 캘린더에는 **날짜 있는** 메모만 보였다. "지난달에 어느 현장에 뭘 적어뒀는데"
 * 를 찾으려면 현장을 하나씩 돌아야 했다.
 *
 * 여기서는 기간 인자 없이 전체를 한 번 읽어(listAll) 날짜가 있든 없든, 어느
 * 현장이든 전부 담고 검색·현장·날짜 유무로 좁힌다. 기간을 주는 순간
 * `scheduled_at IS NULL` 인 행이 통째로 빠지므로 인자를 주지 않는 게 핵심이다.
 *
 * 메모 자체는 별도 엔티티가 아니라 일정의 한 유형(event_type='memo')이라, 상세와
 * 수정은 일정과 똑같은 다이얼로그를 쓴다.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CalendarMonth as CalendarIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  StickyNote2Outlined as MemoIcon,
} from '@mui/icons-material';

import AuthorChip from '../../components/AuthorChip';
import QuickRecordFab from '../../components/QuickRecordFab';
import RecordFormDialog from '../../components/dialogs/RecordFormDialog';
import ScheduleFormDialog, {
  canEditSchedule,
} from '../../components/dialogs/ScheduleFormDialog';
import ScheduleDetailDialog from '../ProjectDashboardPage/dialogs/ScheduleDetailDialog';
import UserProfileMenu from '../../components/UserProfileMenu';
import { SiteScheduleService } from '../../services/SiteScheduleService';
import { SiteProjectService } from '../../services/SiteProjectService';
import { markdownToPlainText } from '../../utils/markdown';
import { Project } from '../../models/Project';
import {
  AllSiteSchedule,
  MEMO_EVENT_TYPE,
  compareByScheduleTime,
  hasScheduleDate,
} from '../../models/SiteSchedule';

/** 날짜 유무 필터. 메모의 성질상 이 축이 가장 자주 쓰인다. */
type DateMode = 'all' | 'undated' | 'dated';

/** 정렬 축. 날짜가 없는 메모가 많아 기본은 작성 순서다. */
type SortMode = 'recent' | 'date';

const DATE_MODE_LABEL: Record<DateMode, string> = {
  all: '전체',
  undated: '날짜 없음',
  dated: '날짜 있음',
};

const formatDate = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('ko-KR', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatCreated = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric' });
};

/** 현장 없는 공통 메모의 필터 키. site_project_id 가 null 인 행을 가리킨다. */
const COMMON_SITE_KEY = 'common';

/** 화면에 쓸 현장 이름 — 현장 없는 공통 메모는 〈공통〉으로 보인다. */
const siteLabel = (m: { site_project_id: number | null; site_name: string }): string =>
  m.site_project_id == null ? '공통' : m.site_name;

/** 필터 키 — null 을 String() 하면 'null' 이 되어 값이 새므로 따로 잡는다. */
const siteKey = (m: { site_project_id: number | null }): string =>
  m.site_project_id == null ? COMMON_SITE_KEY : String(m.site_project_id);

/** 검색 대상 문자열 — 제목·내용·담당자·현장·위치를 한 줄로 합쳐 훑는다. */
const haystack = (m: AllSiteSchedule): string =>
  [m.title, markdownToPlainText(m.description ?? ''), m.assignee, siteLabel(m), m.location]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

const MemoPage: React.FC = () => {
  const navigate = useNavigate();
  const writable = useMemo(() => canEditSchedule(), []);

  const [memos, setMemos] = useState<AllSiteSchedule[]>([]);
  const [sites, setSites] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const [query, setQuery] = useState('');
  const [siteFilter, setSiteFilter] = useState<string>('all');
  const [dateMode, setDateMode] = useState<DateMode>('all');
  const [sort, setSort] = useState<SortMode>('recent');

  const [detail, setDetail] = useState<AllSiteSchedule | null>(null);
  const [editing, setEditing] = useState<AllSiteSchedule | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const reload = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    // 기간 인자를 주지 않는다 — 주면 scheduled_at 이 NULL 인 메모가 전부 빠진다.
    SiteScheduleService.listAll()
      .then((list) => {
        if (cancelled) return;
        setMemos(list.filter((s) => s.event_type === MEMO_EVENT_TYPE));
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : '공지사항 조회 실패');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  // 수정 다이얼로그에서 현장을 옮길 수 있게 목록을 함께 읽는다. 실패해도
  // 메모 조회와는 무관하므로 조용히 넘긴다(현장 고정 상태로 수정만 가능).
  useEffect(() => {
    let cancelled = false;
    SiteProjectService.listProjects()
      .then((list) => {
        // SiteProject.id 는 number, Project.id 는 string — 다이얼로그가 쓰는
        // 모양으로 맞춘다.
        if (!cancelled) setSites(list.map((p) => ({ ...p, id: String(p.id) })) as Project[]);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const undatedCount = useMemo(() => memos.filter((m) => !hasScheduleDate(m)).length, [memos]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = memos.filter((m) => {
      if (siteFilter !== 'all' && siteKey(m) !== siteFilter) return false;
      if (dateMode === 'undated' && hasScheduleDate(m)) return false;
      if (dateMode === 'dated' && !hasScheduleDate(m)) return false;
      if (q && !haystack(m).includes(q)) return false;
      return true;
    });
    return rows.sort((a, b) =>
      sort === 'recent'
        ? new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        : // 날짜 없는 메모는 compareByScheduleTime 이 항상 뒤로 보낸다.
          compareByScheduleTime(a, b, 'asc'),
    );
  }, [memos, query, siteFilter, dateMode, sort]);

  /** 현장 필터 선택지 — 메모가 실제로 있는 현장만 (빈 선택지는 소음이다). */
  const siteOptions = useMemo(() => {
    const seen = new Map<string, string>();
    memos.forEach((m) => seen.set(siteKey(m), siteLabel(m)));
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1], 'ko'));
  }, [memos]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Paper
        elevation={0}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: { xs: 1.5, sm: 4 },
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          position: 'sticky',
          top: 0,
          zIndex: 3,
        }}
      >
        <IconButton onClick={() => navigate('/projects')} size="small" aria-label="뒤로가기">
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <MemoIcon fontSize="small" color="action" />
        <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1, minWidth: 0 }}>
          전체 공지사항
        </Typography>
        <Tooltip title="새로고침">
          <span>
            <IconButton onClick={reload} disabled={loading} size="small" aria-label="새로고침">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <UserProfileMenu />
      </Paper>

      <Container maxWidth="md" sx={{ py: 2, pb: 12 }}>
        {/* 요약 — 전체 몇 건 중 날짜 없는 게 몇 건인지를 가장 자주 확인한다. */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          전체 {memos.length}건 · 날짜 없음 {undatedCount}건 · 모든 현장의 공지사항을 한 곳에서
          봅니다
        </Typography>

        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <TextField
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="제목·내용·담당자·현장으로 검색"
            size="small"
            fullWidth
            inputProps={{ 'aria-label': '메모 검색' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          {/* 좁은 화면에서 필터가 한 줄에 다 들어가지 않으므로 접히게 둔다. */}
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <TextField
              select
              size="small"
              value={siteFilter}
              onChange={(e) => setSiteFilter(e.target.value)}
              sx={{ minWidth: 150 }}
              inputProps={{ 'aria-label': '현장 필터' }}
            >
              <MenuItem value="all">전체 현장</MenuItem>
              {siteOptions.map(([id, name]) => (
                <MenuItem key={id} value={id}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={dateMode}
              onChange={(_, v) => v && setDateMode(v as DateMode)}
            >
              {(['all', 'undated', 'dated'] as DateMode[]).map((m) => (
                <ToggleButton key={m} value={m} sx={{ px: 1.5, py: 0.5, fontSize: '0.75rem' }}>
                  {DATE_MODE_LABEL[m]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={sort}
              onChange={(_, v) => v && setSort(v as SortMode)}
            >
              <ToggleButton value="recent" sx={{ px: 1.5, py: 0.5, fontSize: '0.75rem' }}>
                최근 작성
              </ToggleButton>
              <ToggleButton value="date" sx={{ px: 1.5, py: 0.5, fontSize: '0.75rem' }}>
                날짜순
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Stack>

        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && memos.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={26} />
          </Box>
        ) : filtered.length === 0 ? (
          <Paper variant="outlined" sx={{ px: 3, py: 6, textAlign: 'center' }}>
            <MemoIcon color="disabled" />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {memos.length === 0
                ? '아직 메모가 없습니다.'
                : '조건에 맞는 메모가 없습니다.'}
            </Typography>
            {memos.length > 0 && (
              <Button
                size="small"
                sx={{ mt: 1 }}
                onClick={() => {
                  setQuery('');
                  setSiteFilter('all');
                  setDateMode('all');
                }}
              >
                필터 초기화
              </Button>
            )}
          </Paper>
        ) : (
          <Stack spacing={1.25} data-testid="memo-list">
            {filtered.map((m) => {
              const dated = hasScheduleDate(m);
              return (
                <Paper
                  key={m.id}
                  variant="outlined"
                  role="button"
                  tabIndex={0}
                  onClick={() => setDetail(m)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setDetail(m);
                    }
                  }}
                  sx={{
                    px: 2,
                    py: 1.5,
                    cursor: 'pointer',
                    borderLeft: '4px solid',
                    borderLeftColor: dated ? 'primary.main' : 'divider',
                    '&:hover': { bgcolor: 'action.hover' },
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: -2,
                    },
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    sx={{ mb: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}
                  >
                    <Chip
                      label={siteLabel(m)}
                      size="small"
                      variant="outlined"
                      // 공통 메모는 현장 메모와 한눈에 갈려야 한다.
                      color={m.site_project_id == null ? 'primary' : 'default'}
                      sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                    />
                    {dated ? (
                      <Chip
                        icon={<CalendarIcon sx={{ fontSize: '0.85rem' }} />}
                        label={formatDate(m.scheduled_at)}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    ) : (
                      // 1970-01-01 이나 Invalid Date 가 아니라 명확한 문구여야 한다.
                      <Typography variant="caption" color="text.disabled" fontStyle="italic">
                        날짜 없음
                      </Typography>
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" color="text.disabled">
                      {formatCreated(m.created_at)}
                    </Typography>
                  </Stack>

                  <Typography variant="body2" fontWeight={700}>
                    {m.title}
                  </Typography>

                  {m.description && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        mt: 0.25,
                      }}
                    >
                      {markdownToPlainText(m.description)}
                    </Typography>
                  )}

                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}
                  >
                    {m.assignee && (
                      <Typography variant="caption" color="text.secondary">
                        담당 {m.assignee}
                      </Typography>
                    )}
                    {(m.created_by_name || m.created_by_id) && (
                      <AuthorChip
                        name={m.created_by_name}
                        userId={m.created_by_id}
                        label=""
                        dense
                      />
                    )}
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Container>

      {writable && <QuickRecordFab onClick={() => setCreateOpen(true)} label="메모" />}

      {/* 메모 전용 — kinds 를 하나로 좁혀 종류 선택기를 숨긴다. */}
      <RecordFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        initialKind="memo"
        kinds={['memo']}
        selectableSites={sites}
        onSaved={() => {
          setCreateOpen(false);
          reload();
        }}
      />

      <ScheduleDetailDialog
        open={!!detail}
        schedule={detail}
        onClose={() => setDetail(null)}
        onEdit={
          writable
            ? (s) => {
                setDetail(null);
                setEditing(s as AllSiteSchedule);
              }
            : undefined
        }
      />

      <ScheduleFormDialog
        open={!!editing}
        editing={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          reload();
        }}
        // 전체 현장 화면이라 고정 현장이 없다 — 수정 중 현장 이동도 허용.
        selectableSites={sites}
      />
    </Box>
  );
};

export default MemoPage;
